import React, { useState, useRef, useEffect } from "react";
import { HexColorPicker } from "react-colorful";
import "./CustomToolbar.css";
import useImageUpload from "../hooks/useImageUpload";

const CustomToolbar = ({
  editorRef,
  onToggleSource,
  onToggleFullScreen,
  isFullScreen,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [textColor, setTextColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [wordCount, setWordCount] = useState({ words: 0, characters: 0 });
  const { uploadImage } = useImageUpload();
  const fileInputRef = useRef(null);
  const colorPickerRef = useRef(null);
  const bgColorPickerRef = useRef(null);

  // Current formatting states
  const [currentFormat, setCurrentFormat] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    header: "",
    font: "",
    size: "",
    align: "",
  });

  // Preset color palette
  const presetColors = [
    // First row - Basic colors
    "#000000",
    "#434343",
    "#666666",
    "#999999",
    "#b7b7b7",
    "#cccccc",
    "#d9d9d9",
    "#efefef",
    "#f3f3f3",
    "#ffffff",
    // Second row - Reds
    "#980000",
    "#ff0000",
    "#ff9900",
    "#ffff00",
    "#00ff00",
    "#00ffff",
    "#4a86e8",
    "#0000ff",
    "#9900ff",
    "#ff00ff",
    // Third row - Dark variants
    "#e6b8af",
    "#f4cccc",
    "#fce5cd",
    "#fff2cc",
    "#d9ead3",
    "#d0e0e3",
    "#c9daf8",
    "#cfe2f3",
    "#d9d2e9",
    "#ead1dc",
    // Fourth row - Medium variants
    "#dd7e6b",
    "#ea9999",
    "#f9cb9c",
    "#ffe599",
    "#b6d7a8",
    "#a2c4c9",
    "#a4c2f4",
    "#9fc5e8",
    "#b4a7d6",
    "#d5a6bd",
    // Fifth row - Vibrant colors
    "#cc4125",
    "#e06666",
    "#f6b26b",
    "#ffd966",
    "#93c47d",
    "#76a5af",
    "#6d9eeb",
    "#6fa8dc",
    "#8e7cc3",
    "#c27ba0",
    // Sixth row - Rich colors
    "#a61c00",
    "#cc0000",
    "#e69138",
    "#f1c232",
    "#6aa84f",
    "#45818e",
    "#3c78d8",
    "#3d85c6",
    "#674ea7",
    "#a64d79",
    // Seventh row - Deep colors
    "#85200c",
    "#990000",
    "#b45f06",
    "#bf9000",
    "#38761d",
    "#134f5c",
    "#1155cc",
    "#0b5394",
    "#351c75",
    "#741b47",
    // Eighth row - Very dark
    "#5b0f00",
    "#660000",
    "#783f04",
    "#7f6000",
    "#274e13",
    "#0c343d",
    "#1c4587",
    "#073763",
    "#20124d",
    "#4c1130",
  ];

  // Gradient colors
  const gradientColors = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
    "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    "linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)",
    "linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)",
    "linear-gradient(135deg, #f77062 0%, #fe5196 100%)",
    "linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)",
    "linear-gradient(135deg, #e9defa 0%, #fbfcdb 100%)",
    "linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)",
    "linear-gradient(135deg, #00d2ff 0%, #3a47d5 100%)",
    "linear-gradient(135deg, #f8ff00 0%, #3ad59f 100%)",
    "linear-gradient(135deg, #fddb92 0%, #d1fdff 100%)",
    "linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)",
    "linear-gradient(135deg, #2af598 0%, #009efd 100%)",
  ];

  const formats = [
    { name: "bold", label: "B", title: "Bold" },
    { name: "italic", label: "I", title: "Italic" },
    { name: "underline", label: "U", title: "Underline" },
    { name: "strike", label: "S", title: "Strike" },
  ];

  const fontSizes = [
    "10px",
    "12px",
    "14px",
    "16px",
    "18px",
    "20px",
    "24px",
    "28px",
    "32px",
    "36px",
    "48px",
    "64px",
  ];

  const headings = [
    { value: "", label: "Normal Text" },
    { value: 1, label: "Heading 1" },
    { value: 2, label: "Heading 2" },
    { value: 3, label: "Heading 3" },
    { value: 4, label: "Heading 4" },
    { value: 5, label: "Heading 5" },
    { value: 6, label: "Heading 6" },
  ];

  const fontFamilies = [
    "Arial",
    "Georgia",
    "Times New Roman",
    "Courier New",
    "Verdana",
    "Helvetica",
    "Comic Sans MS",
    "Impact",
    "Trebuchet MS",
    "Palatino",
  ];

  const alignments = [
    { value: "", label: "⬅️", title: "Align Left" },
    { value: "center", label: "↔️", title: "Align Center" },
    { value: "right", label: "➡️", title: "Align Right" },
    { value: "justify", label: "↕️", title: "Justify" },
  ];

  const textTransforms = [
    { value: "uppercase", label: "AA", title: "Uppercase" },
    { value: "lowercase", label: "aa", title: "Lowercase" },
    { value: "capitalize", label: "Aa", title: "Capitalize" },
  ];

  const applyFormat = (format, value = true) => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection();
      if (range) {
        editor.format(format, value);
      }
    }
  };

  const applyColor = (color) => {
    setTextColor(color);
    applyFormat("color", color);
  };

  const applyBgColor = (color) => {
    setBgColor(color);
    applyFormat("background", color);
  };

  const applyCustomStyle = (styleName, value) => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection();
      if (range && range.length > 0) {
        const [leaf] = editor.getLeaf(range.index);
        if (leaf && leaf.parent) {
          const element = leaf.parent.domNode;
          if (element) {
            element.style[styleName] = value;
          }
        }
      }
    }
  };

  const handleUndo = () => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      editor.history.undo();
    }
  };

  const handleRedo = () => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      editor.history.redo();
    }
  };

  const clearFormatting = () => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection();
      if (range) {
        editor.removeFormat(range.index, range.length);
      }
    }
  };

  const insertHorizontalLine = () => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, "\n", "user");
      editor.insertEmbed(range.index + 1, "divider", true, "user");
      editor.setSelection(range.index + 2, "silent");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const imageUrl = await uploadImage(file);

      if (imageUrl) {
        const editor = editorRef.current?.getEditor();
        if (editor) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, "image", imageUrl);
          editor.setSelection(range.index + 1);
        }
      } else {
        alert("Failed to upload image. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Error uploading image. Please try again.");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const alignImage = (alignment) => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection();
      if (range) {
        const [blot] = editor.getLeaf(range.index);
        // Check if current selection is on an image
        if (
          blot &&
          blot.parent &&
          blot.parent.domNode &&
          blot.parent.domNode.tagName === "IMG"
        ) {
          const img = blot.parent.domNode;
          // Remove previous alignment classes
          img.classList.remove("align-left", "align-center", "align-right");
          // Add new alignment class
          if (alignment) {
            img.classList.add(`align-${alignment}`);
          }
          // Also format the paragraph containing the image
          editor.format("align", alignment);
        } else {
          // If not on an image, apply text alignment
          editor.format("align", alignment);
        }
      }
    }
  };

  // Close color pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target)
      ) {
        setShowColorPicker(false);
      }
      if (
        bgColorPickerRef.current &&
        !bgColorPickerRef.current.contains(event.target)
      ) {
        setShowBgColorPicker(false);
      }
    };

    if (showColorPicker || showBgColorPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showColorPicker, showBgColorPicker]);

  // Update toolbar state when selection changes
  useEffect(() => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;

    const updateToolbarState = () => {
      const range = editor.getSelection();
      if (!range) return;

      const format = editor.getFormat(range);

      setCurrentFormat({
        bold: format.bold || false,
        italic: format.italic || false,
        underline: format.underline || false,
        strike: format.strike || false,
        header: format.header || "",
        font: format.font || "",
        size: format.size || "",
        align: format.align || "",
      });

      // Update color states
      if (format.color) {
        setTextColor(format.color);
      }
      if (format.background) {
        setBgColor(format.background);
      }

      // Update word count - if text is selected, show selection count, else show total
      let text;
      if (range.length > 0) {
        // Text is selected - get selected text only
        text = editor.getText(range.index, range.length);
      } else {
        // No selection - get entire document text
        text = editor.getText();
      }

      const words = text
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length;
      const characters = text.replace(/\n/g, "").length;
      setWordCount({ words, characters });
    };

    // Listen to selection change and text change events
    editor.on("selection-change", updateToolbarState);
    editor.on("text-change", updateToolbarState);

    // Initial update
    updateToolbarState();

    return () => {
      editor.off("selection-change", updateToolbarState);
      editor.off("text-change", updateToolbarState);
    };
  }, [editorRef]);

  return (
    <div id="toolbar" className="custom-toolbar">
      {/* Heading Dropdown */}
      <div className="toolbar-section">
        <select
          className="ql-header toolbar-select"
          onChange={(e) => applyFormat("header", e.target.value || false)}
          value={currentFormat.header}
        >
          {headings.map((heading) => (
            <option key={heading.value} value={heading.value}>
              {heading.label}
            </option>
          ))}
        </select>
      </div>

      <div className="toolbar-divider" />

      {/* Undo/Redo */}
      <div className="toolbar-section">
        <button
          className="toolbar-button"
          onClick={handleUndo}
          title="Undo (Ctrl+Z)"
        >
          ↶
        </button>
        <button
          className="toolbar-button"
          onClick={handleRedo}
          title="Redo (Ctrl+Y)"
        >
          ↷
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Basic Formatting */}
      <div className="toolbar-section">
        {formats.map((format) => (
          <button
            key={format.name}
            className={`ql-${format.name} toolbar-button ${
              currentFormat[format.name] ? "ql-active" : ""
            }`}
            title={format.title}
            onClick={() => applyFormat(format.name)}
          >
            {format.label}
          </button>
        ))}
        <button
          className="ql-script toolbar-button"
          value="sub"
          title="Subscript"
          onClick={() => applyFormat("script", "sub")}
        >
          X₂
        </button>
        <button
          className="ql-script toolbar-button"
          value="super"
          title="Superscript"
          onClick={() => applyFormat("script", "super")}
        >
          X²
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Font Family */}
      <div className="toolbar-section">
        <select
          className="ql-font toolbar-select"
          onChange={(e) => applyFormat("font", e.target.value)}
          value={currentFormat.font}
        >
          <option value="">Font Family</option>
          {fontFamilies.map((font) => (
            <option key={font} value={font}>
              {font}
            </option>
          ))}
        </select>
      </div>

      {/* Font Size */}
      <div className="toolbar-section">
        <select
          className="ql-size toolbar-select"
          onChange={(e) => applyFormat("size", e.target.value)}
          value={currentFormat.size}
        >
          <option value="">Size</option>
          {fontSizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      <div className="toolbar-divider" />

      {/* Text Color */}
      <div className="toolbar-section color-picker-section">
        <button
          className="toolbar-button color-button"
          title="Text Color"
          onClick={() => setShowColorPicker(!showColorPicker)}
          style={{ color: textColor }}
        >
          A
        </button>
        {showColorPicker && (
          <div className="color-picker-popup" ref={colorPickerRef}>
            <div className="color-palette-label">Preset Colors</div>
            <div className="color-palette-grid">
              {presetColors.map((color, index) => (
                <button
                  key={index}
                  className="color-swatch"
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    applyColor(color);
                    setShowColorPicker(false);
                  }}
                  title={color}
                />
              ))}
            </div>
            <div className="color-palette-label" style={{ marginTop: "12px" }}>
              Gradient Colors
            </div>
            <div className="color-palette-grid gradient-grid">
              {gradientColors.map((gradient, index) => (
                <button
                  key={index}
                  className="color-swatch gradient-swatch"
                  style={{ background: gradient }}
                  onClick={() => {
                    applyCustomStyle("background", gradient);
                    applyCustomStyle("WebkitBackgroundClip", "text");
                    applyCustomStyle("WebkitTextFillColor", "transparent");
                    applyCustomStyle("backgroundClip", "text");
                    setShowColorPicker(false);
                  }}
                  title="Gradient"
                />
              ))}
            </div>
            <div className="color-palette-label" style={{ marginTop: "12px" }}>
              Custom Color
            </div>
            <HexColorPicker color={textColor} onChange={applyColor} />
            <button
              className="color-picker-close"
              onClick={() => setShowColorPicker(false)}
            >
              ✓
            </button>
          </div>
        )}
      </div>

      {/* Background Color */}
      <div className="toolbar-section color-picker-section">
        <button
          className="toolbar-button color-button"
          title="Background Color"
          onClick={() => setShowBgColorPicker(!showBgColorPicker)}
          style={{ backgroundColor: bgColor }}
        >
          ■
        </button>
        {showBgColorPicker && (
          <div className="color-picker-popup" ref={bgColorPickerRef}>
            <div className="color-palette-label">Preset Colors</div>
            <div className="color-palette-grid">
              {presetColors.map((color, index) => (
                <button
                  key={index}
                  className="color-swatch"
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    applyBgColor(color);
                    setShowBgColorPicker(false);
                  }}
                  title={color}
                />
              ))}
            </div>
            <div className="color-palette-label" style={{ marginTop: "12px" }}>
              Gradient Backgrounds
            </div>
            <div className="color-palette-grid gradient-grid">
              {gradientColors.map((gradient, index) => (
                <button
                  key={index}
                  className="color-swatch gradient-swatch"
                  style={{ background: gradient }}
                  onClick={() => {
                    applyCustomStyle("background", gradient);
                    setShowBgColorPicker(false);
                  }}
                  title="Gradient Background"
                />
              ))}
            </div>
            <div className="color-palette-label" style={{ marginTop: "12px" }}>
              Custom Color
            </div>
            <HexColorPicker color={bgColor} onChange={applyBgColor} />
            <button
              className="color-picker-close"
              onClick={() => setShowBgColorPicker(false)}
            >
              ✓
            </button>
          </div>
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Alignment */}
      <div className="toolbar-section">
        {alignments.map((align) => (
          <button
            key={align.value}
            className={`ql-align toolbar-button ${
              currentFormat.align === align.value ? "ql-active" : ""
            }`}
            title={align.title}
            value={align.value}
            onClick={() => alignImage(align.value)}
          >
            {align.label}
          </button>
        ))}
      </div>

      <div className="toolbar-divider" />

      {/* Letter Spacing */}
      <div className="toolbar-section">
        <label className="toolbar-label">Letter:</label>
        <input
          type="range"
          className="toolbar-slider p-0"
          min="-2"
          max="10"
          step="0.5"
          defaultValue="0"
          onChange={(e) =>
            applyCustomStyle("letterSpacing", `${e.target.value}px`)
          }
          title="Letter Spacing"
        />
      </div>

      {/* Line Height */}
      <div className="toolbar-section">
        <label className="toolbar-label">Line:</label>
        <input
          type="range"
          className="toolbar-slider p-0"
          min="1"
          max="3"
          step="0.1"
          defaultValue="1.5"
          onChange={(e) => applyCustomStyle("lineHeight", e.target.value)}
          title="Line Height"
        />
      </div>

      <div className="toolbar-divider" />

      {/* Text Transform */}
      <div className="toolbar-section">
        {textTransforms.map((transform) => (
          <button
            key={transform.value}
            className="toolbar-button"
            title={transform.title}
            onClick={() => applyCustomStyle("textTransform", transform.value)}
          >
            {transform.label}
          </button>
        ))}
      </div>

      <div className="toolbar-divider" />

      {/* Text Effects */}
      <div className="toolbar-section">
        <button
          className="toolbar-button"
          title="Text Shadow"
          onClick={() =>
            applyCustomStyle("textShadow", "2px 2px 4px rgba(0,0,0,0.5)")
          }
        >
          Shadow
        </button>
        <button
          className="toolbar-button"
          title="Remove Effects"
          onClick={() => {
            applyCustomStyle("textShadow", "none");
            applyCustomStyle("textTransform", "none");
          }}
        >
          Clear
        </button>
      </div>

      {/* Lists and Indentation */}
      <div className="toolbar-section">
        <button
          className="ql-list toolbar-button"
          value="ordered"
          title="Ordered List"
        >
          1.
        </button>
        <button
          className="ql-list toolbar-button"
          value="bullet"
          title="Bullet List"
        >
          •
        </button>
        <button
          className="ql-indent toolbar-button"
          value="-1"
          title="Decrease Indent"
        >
          ◀
        </button>
        <button
          className="ql-indent toolbar-button"
          value="+1"
          title="Increase Indent"
        >
          ▶
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Code & Quotes */}
      <div className="toolbar-section">
        <button
          className="ql-code-block toolbar-button"
          title="Code Block"
          onClick={() => applyFormat("code-block")}
        >
          {"</>"}
        </button>
        <button
          className="ql-blockquote toolbar-button"
          title="Blockquote"
          onClick={() => applyFormat("blockquote")}
        >
          ❝❞
        </button>
        <button
          className="toolbar-button"
          title="Insert Horizontal Line"
          onClick={insertHorizontalLine}
        >
          ―
        </button>
        <button
          className="toolbar-button"
          title="Clear Formatting"
          onClick={clearFormatting}
        >
          🗑️
        </button>
      </div>

      {/* Links and Images */}
      <div className="toolbar-section">
        <button className="ql-link toolbar-button" title="Insert Link">
          🔗
        </button>
        <button
          className="toolbar-button"
          title="Upload Image"
          onClick={handleImageButtonClick}
          disabled={uploadingImage}
        >
          {uploadingImage ? "⏳" : "🖼️"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageUpload}
        />
      </div>

      <div className="toolbar-divider" />

      {/* Source View Toggle */}
      <div className="toolbar-section">
        <button
          className="toolbar-button source-button"
          title="Toggle HTML Source"
          onClick={onToggleSource}
        >
          &lt;/&gt;
        </button>
        <button
          className={`toolbar-button ${isFullScreen ? "ql-active" : ""}`}
          title="Toggle Full Screen"
          onClick={onToggleFullScreen}
        >
          {isFullScreen ? "🗗" : "🗖"}
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Word Count Display */}
      <div className="toolbar-section word-count-section">
        <span className="word-count-label" title="Word Count">
          📝 {wordCount.words} words
        </span>
        <span className="word-count-label" title="Character Count">
          🔤 {wordCount.characters} chars
        </span>
      </div>
    </div>
  );
};

export default CustomToolbar;
